<template>
    <div class="tech-chart-card">
        <div class="tech-chart-card__header">
            <div>
                <p class="tech-chart-card__eyebrow">Campus inventory</p>
                <h3 class="tech-chart-card__title">Assets by category</h3>
            </div>
            <span class="tech-chart-card__badge">{{ totalLabel }}</span>
        </div>
        <div v-if="hasData" ref="chartBody" class="tech-chart-card__body tech-chart-card__body--interactive">
            <apexchart
                type="bar"
                height="320"
                :options="chartOptions"
                :series="series"
                @dataPointSelection="onBarSelect"
            ></apexchart>
        </div>
        <div v-else class="tech-chart-card__empty">No inventory data for this period.</div>
    </div>
</template>

<script>
export default {
    name: "InventoryOverviewChart",
    emits: ["select"],
    props: {
        categories: {
            type: Array,
            default: function () {
                return [];
            },
        },
        values: {
            type: Array,
            default: function () {
                return [];
            },
        },
        moduleKeys: {
            type: Array,
            default: function () {
                return [];
            },
        },
    },
    computed: {
        hasData: function () {
            return Array.isArray(this.values) && this.values.some(function (v) {
                return Number(v) > 0;
            });
        },
        totalLabel: function () {
            var sum = 0;
            if (Array.isArray(this.values)) {
                for (var i = 0; i < this.values.length; i++) {
                    sum += Number(this.values[i]) || 0;
                }
            }
            return sum + " total";
        },
        series: function () {
            return [
                {
                    name: "Assets",
                    data: Array.isArray(this.values) ? this.values : [],
                },
            ];
        },
        chartOptions: function () {
            var self = this;
            return {
                chart: {
                    type: "bar",
                    toolbar: { show: false },
                    fontFamily: "inherit",
                    animations: {
                        enabled: true,
                        easing: "easeinout",
                        speed: 650,
                    },
                    events: {
                        mounted: function () {
                            self.$nextTick(function () {
                                self.bindDataLabelClicks();
                            });
                        },
                        updated: function () {
                            self.$nextTick(function () {
                                self.bindDataLabelClicks();
                            });
                        },
                        animationEnd: function () {
                            self.bindDataLabelClicks();
                        },
                    },
                },
                plotOptions: {
                    bar: {
                        borderRadius: 8,
                        borderRadiusApplication: "end",
                        columnWidth: "52%",
                        distributed: true,
                        dataLabels: {
                            position: "top",
                        },
                    },
                },
                colors: [
                    "#0f766e",
                    "#0d9488",
                    "#14b8a6",
                    "#2dd4bf",
                    "#0891b2",
                    "#0284c7",
                    "#0369a1",
                    "#334155",
                    "#475569",
                    "#64748b",
                ],
                dataLabels: {
                    enabled: true,
                    offsetY: -18,
                    style: {
                        fontSize: "11px",
                        fontWeight: 700,
                        colors: ["#0f172a"],
                    },
                },
                legend: { show: false },
                grid: {
                    borderColor: "#e2e8f0",
                    strokeDashArray: 4,
                    padding: { top: 12, right: 8, bottom: 0, left: 8 },
                },
                xaxis: {
                    categories: Array.isArray(this.categories) ? this.categories : [],
                    labels: {
                        style: {
                            colors: "#64748b",
                            fontSize: "11px",
                            fontWeight: 600,
                        },
                        rotate: -28,
                        rotateAlways: false,
                        hideOverlappingLabels: true,
                    },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
                yaxis: {
                    labels: {
                        style: {
                            colors: "#94a3b8",
                            fontSize: "11px",
                        },
                    },
                },
                tooltip: {
                    theme: "light",
                    y: {
                        formatter: function (val) {
                            return val + " items";
                        },
                    },
                },
                states: {
                    hover: {
                        filter: { type: "lighten", value: 0.08 },
                    },
                    active: {
                        allowMultipleDataPointsSelection: false,
                        filter: { type: "darken", value: 0.12 },
                    },
                },
            };
        },
    },
    beforeUnmount: function () {
        this.unbindDataLabelClicks();
    },
    methods: {
        emitAtIndex: function (index) {
            var categories = Array.isArray(this.categories) ? this.categories : [];
            var keys = Array.isArray(this.moduleKeys) ? this.moduleKeys : [];
            if (index < 0 || index >= categories.length) {
                return;
            }

            var key = keys[index] || null;
            if (!key) {
                return;
            }

            this.$emit("select", {
                index: index,
                category: categories[index],
                key: key,
                count: Array.isArray(this.values) ? Number(this.values[index]) || 0 : 0,
            });
        },
        onBarSelect: function (event, chartContext, config) {
            var index =
                config && typeof config.dataPointIndex === "number"
                    ? config.dataPointIndex
                    : -1;
            this.emitAtIndex(index);
        },
        unbindDataLabelClicks: function () {
            var root = this.$refs.chartBody;
            if (!root) {
                return;
            }
            var clickables = root.querySelectorAll("[data-inventory-chart-click]");
            for (var i = 0; i < clickables.length; i++) {
                clickables[i].onclick = null;
                clickables[i].removeAttribute("data-inventory-chart-click");
            }
        },
        bindDataLabelClicks: function () {
            var root = this.$refs.chartBody;
            if (!root || !this.hasData) {
                return;
            }

            this.unbindDataLabelClicks();

            var texts = root.querySelectorAll(
                ".apexcharts-datalabels text, .apexcharts-data-labels text, .apexcharts-datalabel text"
            );
            var self = this;
            for (var i = 0; i < texts.length; i++) {
                (function (el, index) {
                    el.style.cursor = "pointer";
                    el.setAttribute("data-inventory-chart-click", "1");
                    el.onclick = function (e) {
                        if (e && e.stopPropagation) {
                            e.stopPropagation();
                        }
                        self.emitAtIndex(index);
                    };
                })(texts[i], i);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "./chart-card.scss";

.tech-chart-card__body--interactive :deep(.apexcharts-bar-area),
.tech-chart-card__body--interactive :deep(.apexcharts-series path),
.tech-chart-card__body--interactive :deep(.apexcharts-datalabel),
.tech-chart-card__body--interactive :deep(.apexcharts-datalabels text) {
    cursor: pointer;
}
</style>
